// Port of `BookMatter` (SuiteModel/BookMatter.swift): the pages around a book's text and the documents a submission
// needs, each marked by the `book-matter` metadata field, and `fill`, which puts a compile's values into their
// placeholders. Adding the folders to a project (`ensureBookMatter`) stays with the store.
import type { ProjectKind } from './scriptFormat.ts';
import { stableUUID } from './hashing.ts';
import { trimCFWhitespace } from './unicode.ts';

export type BookMatter =
  // Folders
  | 'front-matter' | 'back-matter' | 'submission' | 'post-matter'
  // Front matter, in book order
  | 'half-title' | 'title-page' | 'copyright' | 'dedication' | 'epigraph' | 'contents'
  // A stage play's front matter (after the title page); a musical's songs
  | 'cast-list' | 'musical-numbers' | 'setting'
  // Back matter
  | 'authors-note' | 'acknowledgements' | 'about-the-author' | 'also-by'
  // Submission
  | 'synopsis' | 'pitch' | 'logline' | 'query-letter' | 'author-bio' | 'comparable-titles'
  // A stage play's submission documents
  | 'development-history'
  // A journal's opening and sign-off
  | 'post-opening' | 'post-sign-off';

export type BookMatterPlacement = 'folder' | 'front' | 'back' | 'submission' | 'post';

/** `BookMatter.allCases`, in declaration order. */
export const BOOK_MATTER_CASES: readonly BookMatter[] = [
  'front-matter', 'back-matter', 'submission', 'post-matter',
  'half-title', 'title-page', 'copyright', 'dedication', 'epigraph', 'contents', 'cast-list', 'musical-numbers', 'setting',
  'authors-note', 'acknowledgements', 'about-the-author', 'also-by',
  'synopsis', 'pitch', 'logline', 'query-letter', 'author-bio', 'comparable-titles', 'development-history',
  'post-opening', 'post-sign-off',
];

/** `BookMatter.stageOnly`: the pages only a script for the stage or the air has (a play's, a musical's, a radio play's). */
const STAGE_ONLY: ReadonlySet<BookMatter> = new Set(['cast-list', 'musical-numbers', 'setting', 'development-history']);

const TITLES: Record<BookMatter, string> = {
  'front-matter': 'Front Matter', 'back-matter': 'Back Matter', submission: 'Submission', 'post-matter': 'Opening & Sign-off',
  'half-title': 'Half Title', 'title-page': 'Title Page', copyright: 'Copyright', dedication: 'Dedication', epigraph: 'Epigraph',
  contents: 'Contents', 'cast-list': 'Cast of Characters', 'musical-numbers': 'Musical Numbers', setting: 'Setting', 'authors-note': "Author's Note", acknowledgements: 'Acknowledgements', 'about-the-author': 'About the Author',
  'also-by': 'Also By', synopsis: 'Synopsis', pitch: 'Pitch', logline: 'Logline', 'query-letter': 'Query Letter',
  'author-bio': 'Author Bio', 'comparable-titles': 'Comparable Titles', 'development-history': 'Development History',
  'post-opening': 'Opening', 'post-sign-off': 'Sign-off',
};

const COPYRIGHT_TEMPLATE = `Published by {publisher}\\
{publisherAddress}\\
{publisherWebsite}

First published {firstPublished}

Copyright © {copyrightYear} {copyrightHolder}

{moralRights}

{rightsNotice}

{fictionNotice}

{catalogueRecord}

ISBN {isbnPaperback} (paperback)\\
ISBN {isbnHardcover} (hardcover)\\
ISBN {isbnEbook} (ebook)\\
ISBN {isbnAudiobook} (audiobook)

Edited by {editor}\\
Cover design by {coverDesigner}\\
Cover image: {coverImage}\\
Typeset in {typeface}

{otherNotices}

{edition}\\
{printingLine}

{authorWebsite}`;

const PLACEHOLDER = /\{([A-Za-z]+)\}/g;

/** `BookMatter.guidance`: what the document is for, as its synopsis. */
const GUIDANCE: Record<BookMatter, string> = {
  'front-matter': "Pages before the story. Book compiles include what's written here, in this order; empty documents are left out.",
  'back-matter': "Pages after the story. Book compiles include what's written here, in this order; empty documents are left out.",
  submission: 'What agents and publishers ask for alongside the manuscript. Never compiled with the book.',
  'half-title': 'The title alone, on the first right-hand page of a printed book.',
  'title-page': 'Title and author. {title} and {author} are filled in when you compile.',
  copyright: 'The copyright notice, on the back of the title page. Add the ISBN when you have one.',
  dedication: 'A line or two, for whoever the book is for. Left out while empty.',
  epigraph: 'A quotation that sets the tone, with its source. Left out while empty.',
  contents: 'Generated when you compile: the parts and chapters, with page numbers in print and links in an ebook.',
  'cast-list': "The characters, most important first, each with a line about them; note any doubling. Left empty, it's made from the cast when you compile.",
  setting: 'Where and when the play happens: the place, then the time.',
  'musical-numbers': "The songs, act by act, numbered, with who sings each. Left empty, it's made from the songs when you compile.",
  'authors-note': 'A note to the reader about the book. Left out while empty.',
  acknowledgements: 'Thanks to the people who helped. Left out while empty.',
  'about-the-author': 'A short biography, in the third person. Left out while empty.',
  'also-by': 'Your other books, one to a line. Left out while empty.',
  synopsis: 'The whole story in a page or two, ending included, in the present tense.',
  pitch: 'The book in a paragraph or two: character, want, obstacle, stakes.',
  logline: 'The book in one sentence.',
  'query-letter': 'The letter to an agent: hook, pitch, the book\'s details (title, genre, word count), comparables and a short bio. {title} and {words} are filled in when you compile.',
  'author-bio': 'A short biography for a query or proposal.',
  'comparable-titles': 'Two or three recent books this one sits beside, and why.',
  'development-history': 'Readings, workshops, productions and awards, most recent first, for theatres and competitions.',
  'post-matter': 'The text every post opens and closes with, put around each post when you share or publish it.',
  'post-opening': 'Before every post. {number} is the post\'s number, {title} its title, {date} its publish date, {journal} the journal\'s name, {author} yours.',
  'post-sign-off': 'After every post: how you sign off. The same placeholders as the opening. Left out while empty.',
};

/** `BookMatter.guidance(for:)`: a play's, a script's and a radio play's own words where they differ. */
const KIND_GUIDANCE: Partial<Record<ProjectKind, Partial<Record<BookMatter, string>>>> = {
  'stage-play': {
    submission: 'What theatres, literary managers and competitions ask for alongside the play. Never compiled with it.',
    synopsis: 'The whole play in a page, ending included, in the present tense.',
    pitch: "The play in a paragraph or two: who it's about, what they want, what's in the way, what's at stake.",
    logline: 'The play in one sentence.',
    'query-letter': 'The cover letter to a theatre, literary manager or competition: what the play is, its length, cast size and setting, where it has been read or staged, and a short bio. {title} is filled in when you compile.',
    'author-bio': 'A short biography for a cover letter or submission.',
  },
  screenplay: {
    submission: 'What agents, managers and producers ask for alongside the script. Never compiled with it.',
    pitch: 'The script in a paragraph or two: character, want, obstacle, stakes.',
    logline: 'The script in one sentence.',
    'query-letter': 'The letter to an agent, manager or producer: hook, pitch, the script\'s details (title, genre, pages), comparables and a short bio. {title} is filled in when you compile.',
    'author-bio': 'A short biography for a cover letter or submission.',
  },
  'radio-play': {
    submission: 'What producers, commissioners and competitions ask for alongside the radio play. Never compiled with it.',
    synopsis: 'The whole play in a page, ending included, in the present tense.',
    pitch: "The radio play in a paragraph or two: who it's about, what they want, what's in the way, what's at stake.",
    logline: 'The radio play in one sentence.',
    'query-letter': 'The covering letter to a producer or a drama competition: what the play is, its length in minutes, its cast, and a short bio. {title} is filled in when you compile.',
    'author-bio': 'A short biography for a cover letter or submission.',
  },
};

/** `BookMatter.otherTitles`: other titles an imported page goes by. */
const OTHER_TITLES: Partial<Record<BookMatter, string[]>> = {
  'cast-list': ['Characters', 'Cast', 'Cast List', 'Dramatis Personae'],
  setting: ['Time and Place', 'Place and Time'],
  'musical-numbers': ['Songs', 'Song List', 'List of Songs'],
};

export const BookMatter = {
  /** Fragment metadata key marking a book-matter document or folder. */
  field: 'book-matter',
  all: BOOK_MATTER_CASES,
  copyrightTemplate: COPYRIGHT_TEMPLATE,

  /** `BookMatter(rawValue:)`. */
  from(raw: string | null | undefined): BookMatter | null {
    return raw != null && (BOOK_MATTER_CASES as readonly string[]).includes(raw) ? (raw as BookMatter) : null;
  },

  placement(m: BookMatter): BookMatterPlacement {
    switch (m) {
      case 'front-matter': case 'back-matter': case 'submission': case 'post-matter': return 'folder';
      case 'half-title': case 'title-page': case 'copyright': case 'dedication': case 'epigraph': case 'contents':
      case 'cast-list': case 'musical-numbers': case 'setting': return 'front';
      case 'authors-note': case 'acknowledgements': case 'about-the-author': case 'also-by': return 'back';
      case 'post-opening': case 'post-sign-off': return 'post';
      default: return 'submission';
    }
  },

  folder(m: BookMatter): BookMatter | null {
    switch (BookMatter.placement(m)) {
      case 'folder': return null;
      case 'front': return 'front-matter';
      case 'back': return 'back-matter';
      case 'submission': return 'submission';
      case 'post': return 'post-matter';
    }
  },

  /** `folders(for:)`: the folders a project of this kind gets. */
  folders(kind: ProjectKind): BookMatter[] {
    switch (kind) {
      case 'manuscript': return ['front-matter', 'back-matter', 'submission'];
      case 'screenplay': return ['submission'];
      case 'stage-play': case 'radio-play': return ['front-matter', 'submission'];
      case 'journal': return ['front-matter', 'back-matter', 'post-matter'];
    }
  },

  stageOnly: STAGE_ONLY,

  /** `documents(for:musical:)`: a folder's documents for a project of this kind, in order. A play's Front Matter is its
   *  title, cast and setting pages (a musical's its songs too), and it has no comparable titles; a radio play's is its
   *  title and cast pages. Nothing else has these pages. */
  documents(folder: BookMatter, kind: ProjectKind = 'manuscript', musical = false): BookMatter[] {
    const all = BOOK_MATTER_CASES.filter((m) => BookMatter.folder(m) === folder);
    if (kind === 'stage-play') {
      if (folder === 'front-matter') return musical ? ['title-page', 'cast-list', 'musical-numbers', 'setting'] : ['title-page', 'cast-list', 'setting'];
      if (folder === 'submission') return all.filter((m) => m !== 'comparable-titles');
      return all;
    }
    if (kind === 'radio-play') {
      if (folder === 'front-matter') return ['title-page', 'cast-list'];
      if (folder === 'submission') return all.filter((m) => m !== 'comparable-titles' && m !== 'development-history');
      return all;
    }
    return all.filter((m) => !STAGE_ONLY.has(m));
  },

  /** `belongs(to:musical:)`: whether a project of this kind has the page. */
  belongs(m: BookMatter, kind: ProjectKind, musical = true): boolean {
    return BookMatter.folders(kind).some((f) => BookMatter.documents(f, kind, musical).includes(m));
  },

  title(m: BookMatter): string { return TITLES[m]; },

  /** `otherTitles`. */
  otherTitles(m: BookMatter): string[] { return OTHER_TITLES[m] ?? []; },

  /** `guidance(for:)`: what the document is for, in the words of the project's kind (its synopsis). */
  guidance(m: BookMatter, kind: ProjectKind = 'manuscript'): string { return KIND_GUIDANCE[kind]?.[m] ?? GUIDANCE[m]; },

  /** The heading printed in the book (placeholders filled at compile). */
  printedTitle(m: BookMatter): string { return m === 'also-by' ? 'Also by {author}' : TITLES[m]; },

  /** The text a new document starts with. */
  template(m: BookMatter): string {
    switch (m) {
      case 'half-title': return '{title}';
      case 'title-page': return '{title}\n\n{author}';
      case 'copyright': return COPYRIGHT_TEMPLATE;
      case 'post-opening': return '*{journal}* No. {number}';
      default: return '';
    }
  },

  /** Whether compiles take it. */
  compiled(m: BookMatter): boolean {
    const p = BookMatter.placement(m);
    return p === 'front' || p === 'back' || p === 'post';
  },

  /** Filled in by compile rather than written: the document's text is ignored. */
  isGenerated(m: BookMatter): boolean { return m === 'contents'; },

  /** `stableID(_:_:)`: this project's copy's id. */
  stableID(m: BookMatter, projectID: string, purpose = 'fragment'): string {
    return stableUUID(`${projectID}|book-matter|${purpose}|${m}`);
  },

  /** `fill(_:title:author:shortTitle:year:words:extra:)`. */
  fillBook(text: string, o: { title: string; author: string | null; shortTitle: string; year: number; words?: string; extra?: Record<string, string> }): string {
    const values: Record<string, string> = { ...(o.extra ?? {}) };
    values.title = o.title;
    values.words = o.words ?? '';
    values.author = o.author ?? '';
    values.shortTitle = o.shortTitle;
    values.year = String(o.year);
    return BookMatter.fill(text, values);
  },

  /** `fill(_:values:)`: each known `{name}` replaced; a line whose known placeholders are all blank is left out, and the
   *  paragraphs are then tidied (empty ones and dangling hard breaks go). Unknown braces stay as written, and so does a
   *  merge tag in doubled braces (`{{title}}`). */
  fill(text: string, values: Record<string, string>): string {
    if (!text.includes('{')) return text;
    let dropped = false;
    const lines: string[] = [];
    for (const line of text.split('\n')) {
      const value = (name: string, at: number, length: number): string | null =>
        line[at - 1] === '{' || line[at + length] === '}' || !Object.prototype.hasOwnProperty.call(values, name) ? null : values[name];
      const known = [...line.matchAll(PLACEHOLDER)].map((m) => value(m[1], m.index!, m[0].length)).filter((v) => v !== null);
      if (known.length && known.every((v) => trimCFWhitespace(v) === '')) {
        dropped = true;
        continue;
      }
      lines.push(line.replace(PLACEHOLDER, (whole, name: string, at: number) => value(name, at, whole.length) ?? whole));
    }
    const filled = lines.join('\n');
    if (!dropped) return filled;
    return filled.split('\n\n').flatMap((paragraph) => {
      let p = paragraph.replace(/^[\n\r\u0085\u2028\u2029\u000b\u000c]+|[\n\r\u0085\u2028\u2029\u000b\u000c]+$/g, '');
      while (p.endsWith('\\')) p = p.slice(0, -1);
      return trimCFWhitespace(p) === '' ? [] : [p];
    }).join('\n\n');
  },
};
